import { AutocompleteChangeDetails, AutocompleteChangeReason } from '@mui/material/Autocomplete'
import { Category } from '@dtx-company/flow-codegen/src/code/generated.types'
import { PrintStoreTemplatesFilterProps, TemplateFilterSection } from './types'
import { UseAutocompleteProps } from '@mui/material/useAutocomplete'
import {
  addSelectedCategory,
  getTemplateFiltersKey,
  removeSelectedCategory,
  resetSelectedCategories
} from '@dtx-company/inter-app/src/redux/slices/printStoreApiSlice/printStoreSlice'
import { events } from '@dtx-company/inter-app/src/event-tracking/events/templatesStore'
import { useCodeSelector } from '@dtx-company/inter-app/src/hooks/useCodeSelector'
import { useDispatch } from 'react-redux'
import { usePrintStoreCategoriesQuery } from '@dtx-company/inter-app/src/redux/slices/printStoreApiSlice/printStoreQueries'

const filterBySectionName = (
  categories: Category[],
  sectionName: TemplateFilterSection
): Category[] => {
  return categories.filter(category => category.sectionName === sectionName)
}

const sortByCategoryName = (categories: Category[]): Category[] => {
  return categories.sort((a, b) => {
    if (a.categoryName < b.categoryName) {
      return -1
    } else if (a.categoryName > b.categoryName) {
      return 1
    } else {
      return 0
    }
  })
}

const moveCategoryToTop = (categories: Category[], categoryName: string): Category[] => {
  const targetCategoryIndex = categories.findIndex(
    category => category.categoryName === categoryName
  )
  const targetCategory = { ...categories[targetCategoryIndex] }
  categories.splice(targetCategoryIndex, 1)
  categories.unshift(targetCategory)
  return categories
}

export interface UsePrintStoreTemplatesFiltersReturn {
  categories: Category[]
  selectedCategories: Category[]
  filterLabel: string
  isError: boolean
  isLoading: boolean
  onChangeHandler: NonNullable<UseAutocompleteProps<Category, true, false, false>['onChange']>
  onResetHandler: () => void
  sectionNameToQueryParam: Record<TemplateFilterSection, string>
}

export const usePrintStoreTemplatesFilters = ({
  sectionName,
  clearTemplates
}: PrintStoreTemplatesFilterProps): UsePrintStoreTemplatesFiltersReturn => {
  const dispatch = useDispatch()
  const { isError, isLoading, categories } = usePrintStoreCategoriesQuery(
    { after: '', limit: 1000 },
    {
      // Return memoized list of categories where the sectionName matches the sectionName passed to this hook
      selectFromResult: result => {
        const allCategories = result.data?.getCategories?.categories || []
        const filteredCategories = filterBySectionName(allCategories, sectionName)
        const sortedFilteredCategories = sortByCategoryName(filteredCategories)
        const finalCategoriesList = moveCategoryToTop(sortedFilteredCategories, 'General')
        return {
          ...result,
          categories: finalCategoriesList
        }
      }
    }
  )

  const selectedCategories = useCodeSelector(
    state => state.printStore.selectedCategories[getTemplateFiltersKey(sectionName)]
  )

  const onResetHandler = (): void => {
    dispatch(resetSelectedCategories(categories))
    events.Templates_Reset_CategoryFilter()
  }

  const onChangeHandler: NonNullable<
    UseAutocompleteProps<Category, true, false, false>['onChange']
  > = (
    _event: React.SyntheticEvent<Element, Event>,
    _value: Category[],
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<Category> | undefined
  ): void => {
    clearTemplates()

    switch (reason) {
      case 'selectOption':
        if (!details?.option) break
        dispatch(addSelectedCategory(details.option))
        events.Templates_Clicked_CategoryFilter(details.option.categoryName)
        break
      case 'removeOption':
        if (!details?.option) break
        dispatch(removeSelectedCategory(details.option))
        events.Templates_Deselected_CategoryFilter(details.option.categoryName)
        break
      case 'clear':
        onResetHandler()
        break
    }
  }

  const sectionNameToQueryParam = {
    [TemplateFilterSection.Vertical]: 'industries',
    [TemplateFilterSection.UseCase]: 'useCases'
  }

  const filterLabel = sectionName === TemplateFilterSection.Vertical ? 'Industry' : 'Use case'

  return {
    categories,
    selectedCategories,
    filterLabel,
    isError,
    isLoading,
    onChangeHandler,
    onResetHandler,
    sectionNameToQueryParam
  }
}
